import type {
  SessionUpdate,
  ToolCallContent,
  ToolCallLocation,
  ToolCallStatus,
  ToolKind,
} from '@agentclientprotocol/sdk';
import type { ToolCall, ToolInput } from '@gitlab-org/tui';

type ToolCallSessionUpdate = Extract<SessionUpdate, { sessionUpdate: 'tool_call' }>;
type ToolCallUpdateSessionUpdate = Extract<SessionUpdate, { sessionUpdate: 'tool_call_update' }>;

/**
 * Maps a tool name to the most appropriate ACP `ToolKind` for icon/UX
 * rendering in clients. Falls back to `'other'` for unknown tools.
 */
export function toolKindFor(toolName: string): ToolKind {
  // Exact-name matches first (most specific).
  const exactMap: Record<string, ToolKind> = {
    read_file: 'read',
    read_files: 'read',
    list_dir: 'read',
    find_files: 'search',
    grep: 'search',
    edit_file: 'edit',
    create_file_with_contents: 'edit',
    mkdir: 'edit',
    run_command: 'execute',
    shell_command: 'execute',
    run_git_command: 'execute',
    todo_write: 'think',
  };
  if (toolName in exactMap) return exactMap[toolName];

  // Prefix/substring heuristics for MCP and other dynamic tool names.
  if (toolName.startsWith('read') || toolName.includes('_read')) return 'read';
  if (toolName.startsWith('search') || toolName.includes('_search')) return 'search';
  if (
    toolName.startsWith('write') ||
    toolName.startsWith('edit') ||
    toolName.startsWith('create') ||
    toolName.startsWith('update') ||
    toolName.startsWith('delete') ||
    toolName.startsWith('remove')
  ) {
    return 'edit';
  }
  if (toolName.startsWith('fetch') || toolName.startsWith('get_url')) return 'fetch';
  if (toolName.startsWith('execute') || toolName.startsWith('run')) return 'execute';

  return 'other';
}

/**
 * Extracts file-path locations from a tool call's input so clients can show
 * "currently editing /src/foo.ts" indicators. Returns an empty array when the
 * tool doesn't operate on a specific file.
 */
export function locationsFor(input: ToolInput): ToolCallLocation[] {
  switch (input.tool) {
    case 'read_file':
    case 'edit_file':
    case 'create_file_with_contents':
      return [{ path: input.filepath }];
    case 'read_files':
      return input.filepaths.map((path) => ({ path }));
    case 'list_dir':
      return [{ path: input.directory }];
    case 'mkdir':
      return [{ path: input.path }];
    default:
      return [];
  }
}

function mapToolStatus(stateType: ToolCall['state']['type']): ToolCallStatus {
  switch (stateType) {
    case 'loading':
      return 'in_progress';
    case 'approval_request':
      return 'pending';
    case 'success':
      return 'completed';
    case 'error':
      return 'failed';
    default:
      return 'pending';
  }
}

function toolContentFor(toolCall: ToolCall): ToolCallContent[] | undefined {
  // Intentionally skip empty display content: `output` is a required string, so
  // an empty string means a silent tool (e.g. `mkdir`) produced no output worth
  // showing. Emitting an empty text block would render as a blank row in
  // clients. The raw value is still preserved via `toolRawOutputFor`, so
  // `rawOutput: ''` remains accurate even when we omit `content`.
  if (toolCall.state.type === 'success' && toolCall.state.output) {
    return [{ type: 'content', content: { type: 'text', text: toolCall.state.output } }];
  }
  if (toolCall.state.type === 'error') {
    return [{ type: 'content', content: { type: 'text', text: toolCall.state.error } }];
  }
  return undefined;
}

function toolRawOutputFor(toolCall: ToolCall): unknown {
  if (toolCall.state.type === 'success') {
    return toolCall.state.output ?? null;
  }
  if (toolCall.state.type === 'error') {
    return toolCall.state.error;
  }
  return undefined;
}

/**
 * Human-readable title for a tool call. For command-executing tools the title
 * surfaces the actual command so the user can see what they are approving
 * (e.g. "git status") rather than a bare tool name ("run_git_command"). Every
 * other tool falls back to `toolCall.name`. A general per-tool label map is a
 * deferred follow-up; this only covers the command-executing tools where the
 * missing command is a real approval-safety gap.
 */
export function titleFor(toolCall: ToolCall): string {
  const { input } = toolCall;
  switch (input.tool) {
    case 'run_command':
    case 'shell_command':
      return input.command || toolCall.name;
    case 'run_git_command': {
      if (!input.command) return toolCall.name;
      return input.commandArgs ? `${input.command} ${input.commandArgs}` : input.command;
    }
    default:
      return toolCall.name;
  }
}

/**
 * Builds the `session/update` payload for a tool element: a `tool_call` the
 * first time `toolCall.id` is seen, a `tool_call_update` on every subsequent
 * sighting. Mutates `announcedToolCallIds` to record the first sighting.
 */
export function buildToolCallSessionUpdate(
  toolCall: ToolCall,
  announcedToolCallIds: Set<string>,
): ToolCallSessionUpdate | ToolCallUpdateSessionUpdate {
  const status = mapToolStatus(toolCall.state.type);
  const locations = locationsFor(toolCall.input);
  // Strip the internal `tool` discriminator so it doesn't leak into the
  // client-visible `rawInput`; same pattern used for `toolArgs` in
  // acp_prompt_runner.ts.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured only to exclude `tool` from rawInput
  const { tool: _tool, ...rawInput } = toolCall.input;

  if (!announcedToolCallIds.has(toolCall.id)) {
    announcedToolCallIds.add(toolCall.id);
    return {
      sessionUpdate: 'tool_call',
      toolCallId: toolCall.id,
      title: titleFor(toolCall),
      kind: toolKindFor(toolCall.name),
      status,
      rawInput,
      ...(locations.length > 0 ? { locations } : {}),
    };
  }

  // Re-send rawInput and locations on updates too: on the first (pending)
  // sighting the input is often still `{ tool: 'pending', name }` with no
  // resolvable file paths, so late-arriving input and locations would never
  // reach the client otherwise (e.g. the "currently editing /src/foo.ts"
  // indicator). Recomputing here surfaces them as the call progresses.
  const content = toolContentFor(toolCall);
  const rawOutput = toolRawOutputFor(toolCall);
  return {
    sessionUpdate: 'tool_call_update',
    toolCallId: toolCall.id,
    status,
    rawInput,
    ...(locations.length > 0 ? { locations } : {}),
    ...(content !== undefined ? { content } : {}),
    ...(rawOutput !== undefined ? { rawOutput } : {}),
  };
}
